import { Minus, Plus } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { WEEKDAY_PICKER, frequencyUsesDays } from './helpers';
import type { DayKey } from './helpers';

interface RecurrenceSelectorProps {
  repeatPattern: string;
  onChangeRepeatPattern: (val: string) => void;
  numberOfClasses: string;
  onChangeNumberOfClasses: (val: string) => void;
  seriesDays: DayKey[];
  onChangeSeriesDays: (days: DayKey[] | ((prev: DayKey[]) => DayKey[])) => void;
}

export function RecurrenceSelector({
  repeatPattern,
  onChangeRepeatPattern,
  numberOfClasses,
  onChangeNumberOfClasses,
  seriesDays,
  onChangeSeriesDays,
}: RecurrenceSelectorProps) {
  const handleDecrementClasses = () => {
    const current = parseInt(numberOfClasses, 10) || 1;
    const next = Math.max(1, current - 1);
    onChangeNumberOfClasses(String(next));
  };

  const handleIncrementClasses = () => {
    const current = parseInt(numberOfClasses, 10) || 1;
    const next = Math.min(52, current + 1);
    onChangeNumberOfClasses(String(next));
  };

  const handleClassesChange = (val: string) => {
    if (val === '') {
      onChangeNumberOfClasses('');
      return;
    }
    const parsed = parseInt(val, 10);
    if (isNaN(parsed)) return;
    const clamped = Math.max(1, Math.min(52, parsed));
    onChangeNumberOfClasses(String(clamped));
  };

  const seriesNeedsDays = frequencyUsesDays(repeatPattern);

  return (
    <>
      <div>
        <Label htmlFor="repeat-pattern" className="text-slate-650 dark:text-slate-400 text-xs">
          Repeat Pattern
        </Label>
        <div className="mt-1">
          <select
            id="repeat-pattern"
            value={repeatPattern}
            onChange={(e) => onChangeRepeatPattern(e.target.value)}
            className="h-9 w-full rounded-md border border-slate-350 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:ring-blue-500"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>

      <div>
        <Label htmlFor="num-classes" className="text-slate-655 dark:text-slate-400 text-xs">
          Number of Classes
        </Label>
        <div className="mt-1 flex items-center">
          <button
            type="button"
            onClick={handleDecrementClasses}
            className="flex h-9 w-9 items-center justify-center rounded-l-md border border-r-0 border-slate-300 bg-slate-50 text-slate-505 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-205"
            aria-label="Decrease number of classes"
          >
            <Minus size={16} />
          </button>
          <input
            id="num-classes"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={numberOfClasses}
            onChange={(e) => handleClassesChange(e.target.value)}
            className="h-9 w-14 border border-slate-300 bg-white text-center text-sm font-medium text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
          />
          <button
            type="button"
            onClick={handleIncrementClasses}
            className="flex h-9 w-9 items-center justify-center rounded-r-md border border-l-0 border-slate-300 bg-slate-50 text-slate-505 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-205"
            aria-label="Increase number of classes"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      {seriesNeedsDays && (
        <div className="col-span-1 sm:col-span-2 md:col-span-3 mt-2 border-t border-slate-100 dark:border-slate-800/80 pt-3">
          <Label className="mb-1.5 block text-slate-655 dark:text-slate-400 text-xs">
            Repeat On
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_PICKER.map((day) => {
              const isSelected = seriesDays.includes(day.key);
              return (
                <button
                  key={day.key}
                  type="button"
                  aria-pressed={isSelected}
                  title={day.full}
                  onClick={() => {
                    onChangeSeriesDays((prev) =>
                      isSelected
                        ? prev.filter((k) => k !== day.key)
                        : [...prev, day.key]
                    );
                  }}
                  className={cn(
                    'h-9 rounded-md px-3 text-xs font-medium border transition-all cursor-pointer',
                    isSelected
                      ? 'bg-blue-600 border-blue-600 text-white dark:bg-blue-505 dark:border-blue-505'
                      : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700',
                  )}
                >
                  {day.short}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
